/**
 * Initialize accessory functionality
 * @param {Object} dom - The DOM elements for the accessory module.
 * @param {Object} state - The state object for the accessory module.
 * @param {Function} checkCanvasHasContent - Function to check if the canvas has content.
 * @param {Function} pushState - Function to push the current state.
 *
 * @returns {void}
 */
export default function initAccessories({ dom, state, checkCanvasHasContent, pushState }) {
	const { wrapper, accessoryButtons, accessoryContainer } = dom;
	let cursorAccessoryImg = null;
	let moveCursorAccessory = null;
	let dragEl = null;
	let dragOffsetX = 0;
	let dragOffsetY = 0;
	let wrapperRect = null;

	/**
	 * Check if the device supports touch
	 *
	 * @returns {boolean} - True if the device supports touch, false otherwise.
	 */
	const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

	/**
	 * Deactivate the cursor accessory
	 *
	 * @returns {void}
	 */
	const deactivateCursorAccessory = () => {
		if (!cursorAccessoryImg) return;
		document.removeEventListener('pointermove', moveCursorAccessory);
		cursorAccessoryImg.remove();
		cursorAccessoryImg = null;
	};

	/**
	 * Activate the cursor accessory
	 * @param {string} src - The source URL of the accessory image.
	 * @param {PointerEvent|null} initEvent - The initial pointer event for positioning the accessory.
	 *
	 * @returns {void}
	 */
	const activateCursorAccessory = (src, initEvent = null) => {
		deactivateCursorAccessory();
		cursorAccessoryImg = new Image();
		cursorAccessoryImg.src = src;
		cursorAccessoryImg.className = 'wp-block-mavis-design-a-hairstyle__cursor-accessory';
		Object.assign(cursorAccessoryImg.style, {
			position: 'fixed',
			pointerEvents: 'none',
			width: `${state.currentAccessorySize}px`,
			transform: 'translate(-50%, -50%)',
			zIndex: '9999',
		});
		document.body.appendChild(cursorAccessoryImg);

		/**
		 * Move the cursor accessory image
		 *
		 * @param {PointerEvent} e - The pointer event containing the mouse position.
		 *
		 * @returns {void}
		 */
		moveCursorAccessory = (e) => {
			cursorAccessoryImg.style.left = `${e.clientX}px`;
			cursorAccessoryImg.style.top = `${e.clientY}px`;
			const isFlipped = e.clientX < window.innerWidth / 2;
			cursorAccessoryImg.classList.toggle('flipped', isFlipped);
			cursorAccessoryImg.style.transform = `translate(-50%, -50%) ${isFlipped ? 'scaleX(-1)' : ''}`;
		};

		if (initEvent) moveCursorAccessory(initEvent);
		document.addEventListener('pointermove', moveCursorAccessory);
	};

	accessoryButtons.forEach((btn) => {
		btn.addEventListener('click', (e) => {
			e.stopPropagation();
			// eslint-disable-next-line no-param-reassign
			state.selectedAccessory = btn.src;

			const minSize = 50; // Minimum width in pixels
			const maxSize = 175; // Maximum width in pixels
			const randomSize = Math.floor(Math.random() * (maxSize - minSize + 1)) + minSize;
			// eslint-disable-next-line no-param-reassign
			state.currentAccessorySize = randomSize;

			accessoryButtons.forEach((b) => b.classList.remove('is-active'));
			btn.classList.add('is-active');
			wrapper.querySelector('.mavis-modal')?.classList.remove('is-active');

			if (!isTouchDevice()) {
				// On desktop, attach the accessory to the cursor as before.
				activateCursorAccessory(btn.src, e);
				return;
			}

			// On touch devices, place accessory by default
			pushState();

			const wRect = accessoryContainer.getBoundingClientRect();
			const centerX = wRect.width / 2;
			const centerY = wRect.height / 2;

			const imgEl = new Image();
			imgEl.draggable = false;
			imgEl.src = state.selectedAccessory;
			imgEl.setAttribute('data-placed-accessory', 'true');
			imgEl.classList.add('wp-block-mavis-design-a-hairstyle__canvas-accessory');

			if (e.clientX < window.innerWidth / 2) {
				imgEl.classList.add('flipped');
			}

			Object.assign(imgEl.style, {
				position: 'absolute',
				width: `${state.currentAccessorySize}px`,
				height: 'auto',
				zIndex: '3',
			});
			accessoryContainer.appendChild(imgEl);

			/**
			 * Set the position of the accessory image once it has loaded.
			 *
			 * @returns {void}
			 */
			imgEl.onload = () => {
				imgEl.style.left = `${centerX - imgEl.offsetWidth / 2}px`;
				imgEl.style.top = `${centerY - imgEl.offsetHeight / 2}px`;
				checkCanvasHasContent();
			};

			// eslint-disable-next-line no-param-reassign
			state.selectedAccessory = null;
		});
	});

	/**
	 * Start dragging an accessory
	 *
	 * @param {PointerEvent} e - The pointer event containing the mouse position.
	 *
	 * @returns {void}
	 */
	const startDrag = (e) => {
		if (e.button !== 0) return;
		const img = e.target.closest('img[data-placed-accessory]');
		if (!img) return;

		pushState();

		dragEl = img;
		wrapperRect = accessoryContainer.getBoundingClientRect();
		dragOffsetX = e.clientX - wrapperRect.left - parseFloat(getComputedStyle(img).left);
		dragOffsetY = e.clientY - wrapperRect.top - parseFloat(getComputedStyle(img).top);
		img.setPointerCapture(e.pointerId);
		img.classList.add('is-dragging');
	};

	/**
	 * Handle dragging an accessory
	 *
	 * @param {PointerEvent} e - The pointer event containing the mouse position.
	 *
	 * @returns {void}
	 */
	const doDrag = (e) => {
		if (!dragEl) return;
		let x = e.clientX - wrapperRect.left - dragOffsetX;
		let y = e.clientY - wrapperRect.top - dragOffsetY;
		x = Math.max(0, Math.min(x, wrapperRect.width - dragEl.offsetWidth));
		y = Math.max(0, Math.min(y, wrapperRect.height - dragEl.offsetHeight));
		dragEl.style.left = `${x}px`;
		dragEl.style.top = `${y}px`;
	};

	/**
	 * End dragging an accessory
	 *
	 * @param {PointerEvent} e - The pointer event containing the mouse position.
	 *
	 * @returns {void}
	 */
	const endDrag = (e) => {
		if (!dragEl) return;
		dragEl.classList.remove('is-dragging');
		dragEl.releasePointerCapture(e.pointerId);
		dragEl = null;
		checkCanvasHasContent();
	};

	wrapper.addEventListener('pointerdown', startDrag);
	wrapper.addEventListener('pointermove', doDrag);
	wrapper.addEventListener('pointerup', endDrag);
	wrapper.addEventListener('pointercancel', endDrag);

	return { deactivateCursorAccessory, activateCursorAccessory };
}
